/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-nested-ternary */
import { string, node, object, number, func, oneOfType, oneOf, bool, array, } from 'prop-types';
import React, { useState, useEffect } from 'react';
import Icon from '../icon';
import setClass from '../../common/set-class';
import useAfterMount from '../../hooks/use-after-mount';
const isString = (v) => typeof v === 'string';
const isObject = (v) => typeof v === 'object' && v != null;
const InputCharCount = (props) => {
    const { max, current, className, style, compact, superCompact } = props;
    return (React.createElement("div", { style: style, className: setClass([
            className,
            'b-input-char-count',
            'b-txt-light2',
            compact ? 'b-input-char-count--max-width' : '',
            superCompact ? 'b-input-char-count--super-compact' : '',
        ]) },
        current,
        "/",
        max));
};
InputCharCount.propTypes = {
    max: number,
    current: number,
    className: string,
    style: object,
    compact: bool,
    superCompact: bool,
};
InputCharCount.defaultProps = {
    max: 0,
    current: 0,
    className: null,
    style: {},
    compact: false,
    superCompact: false,
};
const renderLabel = (label, props) => {
    const { disabled, isOptional, id } = props;
    const disClass = 'b-txt-light2 b-txt-input__label--disabled';
    if (!label) {
        return null;
    }
    if (typeof label === 'string') {
        return (React.createElement("label", { className: setClass([
                'b-txt b-txt-input__label',
                disabled ? disClass : '',
            ]), htmlFor: id || '' },
            label,
            isOptional && React.createElement("span", { className: "b-txt-light2" }, " (optional)")));
    }
    return React.cloneElement(label, {
        className: setClass([
            'b-txt',
            'b-txt-input__label',
            props.disabled ? disClass : '',
            label.props.className || '',
        ]),
    });
};
const TextInput = (props) => {
    const { type, value, onChange, style, placeholder, disabled, label, subLabel, maxLength, minLength, readOnly, fieldsGrid, compact, isOptional, superCompact, textArea, kind, className, wrapperClassName, onFocus, onBlur, onInput, validator, onInvalidate, onValidationResult, showMaxLengthMessage, icon, runValidationOn, autoFocus, onIconClick, hideMaxLength, removeValidationOnUnmount, removeIcon, ...rest } = props;
    const afterMount = useAfterMount();
    const [inputType, setInputType] = useState(type);
    const [isPwdMasking, setIsPwdMasking] = useState(true);
    /* eslint-disable-next-line no-unused-vars */
    const [hasEverFocus, setHasEverFocus] = useState(false);
    /* eslint-disable-next-line no-unused-vars */
    const [hasEverBlur, setHasEverBlur] = useState(false);
    const [error, setError] = useState({
        message: '',
        isError: false,
    });
    const [inputRef, setInputRef] = useState();
    const [isMaxLengthMsgTrigger, setIsMaxLengthMsgTrigger] = useState(false);
    const [isFocus, setIsFocus] = useState(!!autoFocus);
    const [hasIcon, setHasIcon] = useState(!removeIcon && Boolean(icon || type === 'password'));
    const toggleIsMaskingPwd = (e) => {
        e.preventDefault();
        setIsPwdMasking((p) => !p);
    };
    // remove validation on unmount
    useEffect(() => () => {
        if (removeValidationOnUnmount && onInvalidate) {
            onInvalidate(false);
        }
    }, []);
    /* determine if we should show an icon */
    useEffect(() => {
        setHasIcon(!removeIcon && Boolean(icon || type === 'password'));
    }, [removeIcon, icon, type]);
    /* toggle input type for pwd mask */
    useEffect(() => {
        if (type !== 'password') {
            return;
        }
        setInputType(isPwdMasking ? 'password' : 'text');
    }, [isPwdMasking]);
    const matchTrigger = (trigger, triggerOpt) => (isString(trigger) && trigger === triggerOpt) ||
        (Array.isArray(trigger) && trigger.includes(triggerOpt));
    const getFailed = (validationResult, onIsAnyInvalid, onResult, trigger, ignoreTriggerCaseMatch) => {
        if (Array.isArray(validationResult)) {
            const isAnyInvalid = validationResult
                .map((vr) => vr.invalid())
                .some(Boolean);
            const matching = validationResult.find((v) => v.invalid());
            if (!matching) {
                setError({
                    isError: false,
                    message: '',
                });
                onIsAnyInvalid(false);
                onResult(validationResult.map((vr) => ({ ...vr, isInvalid: vr.invalid() })));
            }
            else {
                const canValidate = ignoreTriggerCaseMatch || matchTrigger(matching.trigger, trigger);
                onIsAnyInvalid(isAnyInvalid);
                if (!matching.trigger &&
                    (trigger === 'onInput' || trigger === 'onMount')) {
                    setError({
                        isError: matching ? matching.invalid() : false,
                        message: matching ? matching.message : '',
                    });
                    onResult(validationResult.map((vr) => ({ ...vr, isInvalid: vr.invalid() })));
                    return { matching, isAnyInvalid };
                }
                if (canValidate) {
                    setError({
                        isError: matching ? matching.invalid() : false,
                        message: matching ? matching.message : '',
                    });
                    onResult(validationResult.map((vr) => ({ ...vr, isInvalid: vr.invalid() })));
                    return { matching, isAnyInvalid };
                }
            }
        }
        else if (isObject(validationResult)) {
            const res = validationResult.invalid();
            onIsAnyInvalid(res);
            if (!validationResult.trigger &&
                (trigger === 'onInput' || trigger === 'onMount')) {
                onResult({ ...validationResult, isInvalid: res });
                setError({
                    isError: res,
                    message: validationResult.message,
                });
                return { matching: validationResult, isAnyInvalid: res };
            }
            if (matchTrigger(validationResult.trigger, trigger)) {
                onResult({ ...validationResult, isInvalid: res });
                setError({
                    isError: res,
                    message: validationResult.message,
                });
                return { matching: validationResult, isAnyInvalid: res };
            }
        }
        return null;
    };
    const handleFocus = (e) => {
        e.persist();
        setHasEverFocus(true);
        setIsFocus(true);
        onFocus(e);
        /* validation */
        const eve = 'onFocus';
        const vr = validator(e.target.value);
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 5 arguments, but got 4.
        getFailed(vr, onInvalidate, onValidationResult, eve);
        /* handle show max length message */
        if (showMaxLengthMessage && value?.length === maxLength) {
            setIsMaxLengthMsgTrigger(true);
        }
    };
    const handleBlur = (e) => {
        e.persist();
        setHasEverBlur(true);
        setIsFocus(false);
        onBlur(e);
        /* validation */
        const eve = 'onBlur';
        const vr = validator(e.target.value);
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 5 arguments, but got 4.
        getFailed(vr, onInvalidate, onValidationResult, eve);
        /* on blur dont show the maxLength message */
        if (showMaxLengthMessage) {
            setIsMaxLengthMsgTrigger(false);
        }
    };
    const handleOnInput = (e) => {
        e.persist();
        onInput(e);
    };
    const events = ['onBlur', 'onInput', 'onMount', 'onFocus'];
    useEffect(() => {
        if (afterMount) {
            const vr = validator(value);
            getFailed(vr, onInvalidate, onValidationResult, '', true);
            if (showMaxLengthMessage) {
                setIsMaxLengthMsgTrigger(showMaxLengthMessage && value?.length === maxLength);
            }
        }
    }, Array.isArray(validator(value))
        ? validator(value)
            .map((val) => {
            if (Array.isArray(val.trigger)) {
                return val.trigger.filter((d) => !events.includes(d));
            }
            return [];
        })
            .flat()
        : []);
    /* invalidate on mount if trigger set, and on value change after mount. */
    useEffect(() => {
        if (afterMount) {
            const eve = 'onInput';
            const vr = validator(value);
            // @ts-expect-error ts-migrate(2554) FIXME: Expected 5 arguments, but got 4.
            getFailed(vr, onInvalidate, onValidationResult, eve);
            /* also handle show max length message */
            setIsMaxLengthMsgTrigger(showMaxLengthMessage && value?.length === maxLength);
        }
        else {
            const eve = 'onMount';
            const vr = validator(value);
            // @ts-expect-error ts-migrate(2554) FIXME: Expected 5 arguments, but got 4.
            getFailed(vr, onInvalidate, onValidationResult, eve);
        }
    }, [value, ...runValidationOn]);
    useEffect(() => {
        if (autoFocus && inputRef) {
            // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
            inputRef.focus();
        }
    }, [autoFocus, inputRef]);
    const inputComponent = () => {
        const InputCmp = (React.createElement("div", { className: setClass(subLabel && 'b-txt-input--with-sublabel') },
            React.createElement("input", { ref: setInputRef, type: inputType, value: value, onChange: onChange, className: setClass([
                    className,
                    'b-txt-input__elm',
                    isMaxLengthMsgTrigger && '--max-length',
                    kind === 'library' ? 'b-txt-input__elm--library' : '',
                    error.isError ? 'b-txt-input__elm--error' : '',
                    hasIcon ? 'b-txt-input__elm--icon' : '',
                ]), style: style, placeholder: placeholder, disabled: disabled, maxLength: maxLength, minLength: minLength, readOnly: readOnly, onFocus: handleFocus, onBlur: handleBlur, onInput: handleOnInput, ...rest }),
            subLabel && (React.createElement("div", { className: "b-txt b-txt-input__sublabel" }, subLabel))));
        const TextAreaCmp = (React.createElement("textarea", { ref: setInputRef, type: inputType, value: value, onChange: onChange, className: setClass([
                className,
                'b-txt-input__elm',
                isMaxLengthMsgTrigger && '--max-length',
                error.isError ? 'b-txt-input__elm--error' : '',
            ]), style: style, placeholder: placeholder, disabled: disabled, maxLength: maxLength, minLength: minLength, readOnly: readOnly, onFocus: handleFocus, onBlur: handleBlur, onInput: handleOnInput, ...rest }));
        const ErrorTextCmp = error.message && (React.createElement("div", { className: setClass('b-txt-input__err-msg', kind === 'library' && 'b-txt-input__err-msg-library') },
            React.createElement(Icon, { name: "triangle-exclamation", className: "b-txt-input__err-icon" }),
            React.createElement("p", { className: "b-txt b-txt--error" }, error.message)));
        const MaxLengthTextCmp = (customMsg = 'Max character limit') => (React.createElement("div", { className: setClass('b-txt-input__err-msg', kind === 'library' ? 'b-txt-input__err-msg-library' : '', '--max-character') },
            React.createElement(Icon, { name: "triangle-exclamation", className: setClass('b-txt-input__err-icon', kind === 'library' ? 'b-txt-input__err-icon-library' : '', '--max-character') }),
            React.createElement("p", { className: setClass('b-txt', kind === 'library'
                    ? 'b-txt-input__err-txt-library'
                    : 'b-txt-light2', '--max-character') }, customMsg),
            kind === 'library' && (React.createElement(InputCharCount, { className: "b-input-char-count-library --max-character", compact: compact, superCompact: superCompact, max: maxLength, current: !value ? 0 : value.length }))));
        const EyeCmp = (React.createElement("a", { href: "", onClick: toggleIsMaskingPwd },
            React.createElement(Icon, { name: isPwdMasking ? 'eye-slash' : 'eye', size: "sm", className: "b-txt-input__icon" })));
        const IconCmp = (React.createElement("span", { className: setClass(onIconClick && 'b-txt-input-icon-clickable'), onClick: () => {
                if (onIconClick) {
                    onIconClick();
                }
            } },
            React.createElement(Icon, { name: icon, className: "b-txt-input__icon", size: "sm" })));
        return (React.createElement(React.Fragment, null,
            renderLabel(label, props),
            React.createElement("div", { className: setClass([
                    wrapperClassName,
                    'b-txt-input',
                    '--hook',
                    isFocus ? 'b-txt-input--focus' : '',
                    compact ? 'b-txt-input--max-width' : '',
                    superCompact ? 'b-txt-input--super-compact' : '',
                    error.isError ? 'b-txt-input--error-state' : '',
                ]) },
                textArea ? TextAreaCmp : InputCmp,
                hasIcon && icon && IconCmp,
                hasIcon && !icon && type === 'password' && !disabled && EyeCmp,
                error.isError
                    ? ErrorTextCmp
                    : showMaxLengthMessage && isMaxLengthMsgTrigger
                        ? MaxLengthTextCmp()
                        : null),
            maxLength && !hideMaxLength ? (React.createElement(InputCharCount, { compact: compact, superCompact: superCompact, max: maxLength, current: !value ? 0 : value.length })) : null));
    };
    if (fieldsGrid) {
        return (React.createElement("div", { className: setClass([fieldsGrid, 'b-grid-fields', '--hook']) }, inputComponent()));
    }
    return React.createElement(React.Fragment, null, inputComponent());
};
TextInput.propTypes = {
    type: string,
    value: oneOfType([string, number]),
    onChange: func,
    className: string,
    wrapperClassName: string,
    style: object,
    placeholder: string,
    disabled: bool,
    label: oneOfType([node, string]),
    subLabel: oneOfType([node, string]),
    maxLength: number,
    minLength: number,
    readOnly: bool,
    /** wraps the label and the input in a grid and spaces them out. Can be combined
      with a parent div with `b-form-grid` to space out inputs to create a form. */
    fieldsGrid: oneOfType([bool, string]),
    compact: bool,
    superCompact: bool,
    textArea: bool,
    isOptional: bool,
    /** `library` type makes an input look like inputs used as the name of library items. */
    kind: oneOf(['default', 'library']),
    onFocus: func,
    onBlur: func,
    onInput: func,
    /** `validator` is a function that gets called on every input change with the input value. It expects you to either return an object or an array of objects in the form of `{message: <string>, invalid: <function>}`. For more examples and detailed explanation, see [Text Input Guide](/?path=/story/atoms-textinput-text-input-guide--page) */
    validator: func,
    /** `onInvalidate` gets called with the final invalidation result, will be true if any invalidations is true */
    onInvalidate: func,
    onValidationResult: func,
    /** if set, then a message shows up under the text field letting the user know they have reached max character limit */
    showMaxLengthMessage: bool,
    /** if set, will ignore the password type icon */
    icon: string,
    /** overrides showing any icon */
    removeIcon: bool,
    /** an array of state variables to watch out for to trigger validations */
    runValidationOn: array,
    autoFocus: bool,
    onIconClick: func,
    /** if set, will hide the max length counter for maxLength prop */
    hideMaxLength: bool,
    removeValidationOnUnmount: bool,
};
TextInput.defaultProps = {
    type: 'text',
    value: '',
    onChange: () => { },
    disabled: false,
    readOnly: false,
    fieldsGrid: '',
    compact: false,
    superCompact: false,
    textArea: false,
    isOptional: false,
    kind: 'default',
    className: null,
    wrapperClassName: null,
    style: {},
    placeholder: '',
    label: '',
    subLabel: '',
    maxLength: null,
    minLength: null,
    onFocus: () => { },
    onBlur: () => { },
    validator: () => { },
    onInvalidate: () => { },
    onInput: () => { },
    onValidationResult: () => { },
    showMaxLengthMessage: true,
    icon: null,
    removeIcon: false,
    runValidationOn: [],
    autoFocus: false,
    onIconClick: null,
    hideMaxLength: false,
    removeValidationOnUnmount: false,
};
export default TextInput;
export { InputCharCount };
